import { type Registry as Services, service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import { tKey } from 'ember-intl';
import type { PaymentMethod } from 'frontend-payments/utils/constants/payment-method-constants';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';

export type TransactionType = 'invoice' | 'credit_note' | 'debit_note';

export const TRANSACTION_TYPE_TRANSLATION_KEYS = {
  invoice: tKey('payments.invoice.table.header.invoice'),
  credit_note: tKey('payments.invoice.table.credit_note'),
  debit_note: tKey('payments.invoice.table.debit_note'),
} as const satisfies Record<TransactionType, ReturnType<typeof tKey>>;

export const TRANSACTION_STATUS = {
  PAID: 'paid',
  OVERDUE: 'overdue',
  APPLIED: 'applied',
  UNPAID: 'unpaid',
  UNAPPLIED: 'unapplied',
} as const;

export type TransactionStatus =
  (typeof TRANSACTION_STATUS)[keyof typeof TRANSACTION_STATUS];

export const TRANSACTION_STATUS_TRANSLATION_KEYS = {
  [TRANSACTION_STATUS.PAID]: tKey('payments.transaction.status.paid'),
  [TRANSACTION_STATUS.OVERDUE]: tKey('payments.transaction.status.overdue'),
  [TRANSACTION_STATUS.APPLIED]: tKey('payments.transaction.status.applied'),
  [TRANSACTION_STATUS.UNPAID]: tKey('payments.transaction.status.unpaid'),
  [TRANSACTION_STATUS.UNAPPLIED]: tKey('payments.transaction.status.unapplied'),
} as const;

export type TransactionStatusTranslationKey =
  (typeof TRANSACTION_STATUS_TRANSLATION_KEYS)[keyof typeof TRANSACTION_STATUS_TRANSLATION_KEYS];

export default class Transaction extends Model {
  [Type] = 'transaction' as const;

  @attr('string') declare currency: SupportedCurrencies;
  @attr('dayjs') declare dueDate: DayjsTransformReturn | null;
  @attr('string') declare paymentMethod: PaymentMethod | null;
  @attr('string') declare paymentUrl: string | null;
  @attr('string') declare pdfUrl: string;
  @attr('string') declare receiptUrl: string | null;
  @attr('number') declare remainingAmount: number;
  @attr('string') declare status: TransactionStatus;
  @attr('number') declare totalAmount: number;
  @attr('dayjs') declare transactionDate: DayjsTransformReturn;
  @attr('string') declare transactionNumber: string;

  @attr('string') declare transactionType: TransactionType;

  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get badgeType() {
    if (this.status === TRANSACTION_STATUS.PAID) {
      return 'success';
    }
    if (this.status === TRANSACTION_STATUS.OVERDUE) {
      return 'danger';
    }
    return 'neutral';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dueDateFormatted() {
    return (
      this.dueDate?.format('DD MMM YYYY') ?? this.intl.t('general.missing')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get invoiceRemaining() {
    return this.intl.formatNumber(this.remainingAmount, {
      style: 'currency',
      currency: this.currency,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get invoiceTotal() {
    return this.intl.formatNumber(this.totalAmount, {
      style: 'currency',
      currency: this.currency,
    });
  }

  get isCreditNote(): boolean {
    return this.transactionType === 'credit_note';
  }

  get isDebitNote(): boolean {
    return this.transactionType === 'debit_note';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paymentMethodFormatted() {
    return this.paymentMethod ?? this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get statusFormatted() {
    return this.intl.t(TRANSACTION_STATUS_TRANSLATION_KEYS[this.status]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get transactionDateFormatted() {
    return (
      this.transactionDate?.format('DD MMM YYYY') ??
      this.intl.t('general.missing')
    );
  }
}
